/**
 * Moments Live — micro-cenas que acontecem ENQUANTO a pessoa escreve
 * (não recompensas de fim de sessão). Mesma gramática dos overlays em
 * moments.tsx (pill escura, sem backdrop, pointer-events-none, curtas):
 *
 * - <ComboMoment/>: um flare de brasa acima da barra de status a cada
 *   marco de palavras da sessão, com intensidade CRESCENTE por tier
 *   (mesmo efeito, mais brasa).
 * - <FlowMoment/>: uma vinheta quente pulsa nas bordas da tela quando a
 *   pessoa entra em estado de fluxo (escrita contínua e longa).
 */
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useAutoDone } from './moments';

export type ComboTier = 1 | 2 | 3 | 4 | 5;

interface TierConfig {
  /** deslocamentos horizontais das faíscas (px) — leque simétrico, determinístico */
  sparkX: readonly number[];
  /** duração de cada faísca (s) — encurta conforme o tier cresce (mais rápido) */
  sparkDuration: number;
  hasFlame: boolean;
  ringCount: 0 | 1 | 2;
  hasFlash: boolean;
  goldBorder: boolean;
}

/** Leque simétrico determinístico: `n` pontas entre -spread e +spread. */
function fan(n: number, spread: number): readonly number[] {
  if (n <= 1) return [0];
  const step = (spread * 2) / (n - 1);
  return Array.from({ length: n }, (_, i) => -spread + i * step);
}

/** Config paramétrica por tier — mesmo efeito, intensidade crescente. */
const TIERS: Record<ComboTier, TierConfig> = {
  1: { sparkX: fan(1, 0), sparkDuration: 0.9, hasFlame: false, ringCount: 0, hasFlash: false, goldBorder: false },
  2: { sparkX: fan(3, 18), sparkDuration: 0.85, hasFlame: false, ringCount: 0, hasFlash: false, goldBorder: false },
  3: { sparkX: fan(4, 26), sparkDuration: 0.8, hasFlame: true, ringCount: 0, hasFlash: false, goldBorder: false },
  4: { sparkX: fan(6, 34), sparkDuration: 0.72, hasFlame: true, ringCount: 1, hasFlash: false, goldBorder: false },
  5: { sparkX: fan(10, 46), sparkDuration: 0.62, hasFlame: true, ringCount: 2, hasFlash: true, goldBorder: true },
};

const COMBO_DURATION_MS = 1600;
const FLOW_DURATION_MS = 3000;

/** Borda dourada do tier 5 — substitui o anel âmbar padrão da pill. */
const GOLD_PILL_SHADOW = '0 14px 40px rgba(0,0,0,0.55), 0 0 0 1.5px rgba(246,233,200,0.7) inset';

function ComboSpark({ x, delay, duration }: { x: number; delay: number; duration: number }) {
  return (
    <motion.span
      aria-hidden
      initial={{ x: 0, y: 0, opacity: 0, scale: 0.5 }}
      animate={{ x, y: -34 - Math.abs(x) * 0.18, opacity: [0, 1, 0], scale: [0.5, 1, 0.6] }}
      transition={{ delay, duration, ease: 'easeOut' }}
      className="pointer-events-none absolute left-1/2 top-0 text-[10px]"
      style={{ color: 'var(--ember-hot)' }}
    >
      ✦
    </motion.span>
  );
}

function ComboRing({ delay }: { delay: number }) {
  return (
    <motion.span
      aria-hidden
      initial={{ scale: 1, opacity: 0.55 }}
      animate={{ scale: 1.6, opacity: 0 }}
      transition={{ delay, duration: 0.9, ease: 'easeOut' }}
      className="pointer-events-none absolute inset-0 rounded-full border"
      style={{ borderColor: 'var(--ember)' }}
    />
  );
}

/** Chama pequena, mesma linguagem da vela (BornCandle/.fl em moments.tsx). */
function ComboFlame() {
  return (
    <motion.span
      aria-hidden
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.06, type: 'spring', stiffness: 280, damping: 14 }}
      className="relative inline-block"
      style={{ transformOrigin: 'center bottom' }}
    >
      <span
        style={{
          display: 'block',
          width: 9,
          height: 13,
          borderRadius: '50% 50% 50% 50% / 62% 62% 38% 38%',
          background: 'radial-gradient(circle at 50% 78%, #FFF6DC, #F6E9C8 45%, rgba(223,201,147,0))',
          filter: 'drop-shadow(0 0 6px rgba(246,233,200,0.85))',
        }}
      />
    </motion.span>
  );
}

/**
 * Flare de brasa acima da barra de status — marco de palavras da sessão.
 * Mesmo efeito em todos os tiers (faíscas + brasa), intensidade crescente.
 */
export function ComboMoment({ tier, words, onDone }: { tier: ComboTier; words: number; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, COMBO_DURATION_MS);
  const cfg = TIERS[tier];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, transition: { duration: 0.25 } }}
      transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      className="pointer-events-none fixed inset-x-0 bottom-[86px] z-[135] flex justify-center md:bottom-10"
    >
      <div className="relative">
        {cfg.hasFlash && (
          <motion.span
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.25, 0] }}
            transition={{ duration: 0.5, times: [0, 0.4, 1] }}
            className="pointer-events-none absolute -inset-10 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(246,233,200,0.55), transparent 70%)' }}
          />
        )}
        {Array.from({ length: cfg.ringCount }, (_, i) => (
          <ComboRing key={i} delay={i * 0.18} />
        ))}
        {cfg.sparkX.map((x, i) => (
          <ComboSpark key={i} x={x} delay={i * 0.05} duration={cfg.sparkDuration} />
        ))}
        <div
          className="flex items-center gap-2 rounded-full bg-[rgba(13,20,38,0.94)] px-4 py-2 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]"
          style={cfg.goldBorder ? { boxShadow: GOLD_PILL_SHADOW } : undefined}
        >
          {cfg.hasFlame && <ComboFlame />}
          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#DCE3F7]">
            <span style={{ color: 'var(--ember-hot)' }}>+{words}</span>{' '}
            {t('hud.comboLabel', 'palavras nesta sessão')}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * "Você entrou no fluxo" — vinheta quente pulsa nas bordas da tela
 * quando a pessoa escreve sem parar por um bom tempo.
 */
export function FlowMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, FLOW_DURATION_MS);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
      className="pointer-events-none fixed inset-0 z-[136]"
    >
      <motion.span
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.6, 0] }}
        transition={{ duration: 2.8, times: [0, 0.25, 0.7, 1], ease: 'easeInOut' }}
        className="pointer-events-none absolute inset-0"
        style={{ boxShadow: 'inset 0 0 120px 30px rgba(223,201,147,0.22)' }}
      />
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 220, damping: 20 }}
        className="pointer-events-none absolute inset-0 grid place-items-center"
      >
        <div className="flex items-center gap-3 rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
          <span aria-hidden className="text-[11px]" style={{ color: 'var(--ember-hot)' }}>
            ✦
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#DCE3F7]">
            {t('hud.flowTitle', 'você entrou no fluxo')}
          </span>
          <span aria-hidden className="text-[11px]" style={{ color: 'var(--ember-hot)' }}>
            ✦
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}


/* ─────────── rajada: velocidade de escrita, riscos de brasa nas bordas ───────────
   tier 1: borda esquerda · tier 2: as duas laterais · tier 3: perímetro + pill */
const BURST_STREAKS = [0.12, 0.38, 0.64, 0.86];

export function BurstMoment({ tier, onDone }: { tier: 1 | 2 | 3; onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 1700);
  const sides: Array<'left' | 'right' | 'top'> = tier >= 3 ? ['left', 'right', 'top'] : tier === 2 ? ['left', 'right'] : ['left'];
  return (
    <div className="pointer-events-none fixed inset-0 z-[134]" aria-hidden>
      {sides.map((side) =>
        BURST_STREAKS.map((pos, i) => {
          const vertical = side !== 'top';
          return (
            <motion.span
              key={`${side}-${i}`}
              initial={vertical ? { y: '110vh', opacity: 0 } : { x: '-14vw', opacity: 0 }}
              animate={vertical ? { y: '-24vh', opacity: [0, 1, 0] } : { x: '112vw', opacity: [0, 1, 0] }}
              transition={{ delay: i * 0.09, duration: 0.9, ease: [0.3, 0, 0.5, 1] }}
              className="absolute"
              style={
                vertical
                  ? {
                      [side]: `${2 + (i % 2) * 5}px`,
                      top: 0,
                      width: 2,
                      height: `${11 + (i % 3) * 5}vh`,
                      background: 'linear-gradient(180deg, transparent, var(--ember-hot), transparent)',
                      filter: 'drop-shadow(0 0 6px rgba(246,233,200,0.8))',
                    }
                  : {
                      top: `${2 + (i % 2) * 5}px`,
                      left: `${pos * 100}%`,
                      height: 2,
                      width: '12vw',
                      background: 'linear-gradient(90deg, transparent, var(--ember-hot), transparent)',
                      filter: 'drop-shadow(0 0 6px rgba(246,233,200,0.8))',
                    }
              }
            />
          );
        })
      )}
      {tier >= 3 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: [0, 1, 1, 0], y: 0 }}
          transition={{ duration: 1.6, times: [0, 0.2, 0.8, 1] }}
          className="absolute inset-x-0 bottom-[86px] flex justify-center md:bottom-10"
        >
          <span className="rounded-full bg-[rgba(13,20,38,0.94)] px-4 py-2 font-mono text-[9px] uppercase tracking-[0.24em] text-[#F6E9C8] shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(223,201,147,0.4)_inset]">
            ✦ {t('hud.burstTitle', 'rajada de palavras')}
          </span>
        </motion.div>
      )}
    </div>
  );
}

/* ─────────── fluxo profundo (30min): a brasa respira e sobe ─────────── */
const DEEP_EMBERS = Array.from({ length: 12 }, (_, i) => ({
  x: 4 + ((i * 37) % 92), d: (i % 6) * 0.22, s: 3 + (i % 3),
}));

export function DeepFlowMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 4200);
  return (
    <div className="pointer-events-none fixed inset-0 z-[134]" aria-hidden>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0.7, 0] }}
        transition={{ duration: 4, times: [0, 0.25, 0.7, 1] }}
        className="absolute inset-0"
        style={{ boxShadow: 'inset 0 0 160px 44px rgba(223,201,147,0.28)' }}
      />
      {DEEP_EMBERS.map((e, i) => (
        <motion.span
          key={i}
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: '-46vh', opacity: [0, 0.9, 0] }}
          transition={{ delay: 0.3 + e.d, duration: 2.6, ease: 'easeOut' }}
          className="absolute bottom-0 rounded-full"
          style={{
            left: `${e.x}%`, width: e.s, height: e.s,
            background: '#F6E9C8', boxShadow: '0 0 8px 2px rgba(246,233,200,0.7)',
          }}
        />
      ))}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: 0 }}
        transition={{ duration: 3.6, times: [0, 0.15, 0.8, 1] }}
        className="absolute inset-x-0 bottom-[86px] flex justify-center md:bottom-10"
      >
        <span className="rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#DCE3F7] shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(223,201,147,0.35)_inset]">
          ✦ {t('hud.deepflowTitle', 'fluxo profundo — 30 minutos')} ✦
        </span>
      </motion.div>
    </div>
  );
}

/* ─────────── o transe (60min): o céu atravessa a tela ─────────── */
const TRANCE_STARS = Array.from({ length: 22 }, (_, i) => ({
  x: ((i * 47) % 100), y: 4 + ((i * 31) % 34), d: (i % 11) * 0.18, s: 1.5 + (i % 3),
}));

export function TranceMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 5600);
  return (
    <div className="pointer-events-none fixed inset-0 z-[134] overflow-hidden" aria-hidden>
      {/* véu de aurora no topo */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.55, 0.4, 0] }}
        transition={{ duration: 5.2, times: [0, 0.3, 0.7, 1] }}
        className="absolute inset-x-0 top-0 h-[26vh]"
        style={{ background: 'linear-gradient(180deg, rgba(94,234,212,0.16), rgba(167,139,250,0.08) 55%, transparent)' }}
      />
      {/* o céu em movimento */}
      <motion.div
        initial={{ x: '6vw' }}
        animate={{ x: '-6vw' }}
        transition={{ duration: 5.4, ease: 'linear' }}
        className="absolute inset-0"
      >
        {TRANCE_STARS.map((st, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0.9, 0] }}
            transition={{ delay: st.d, duration: 4.4, times: [0, 0.2, 0.8, 1] }}
            className="absolute rounded-full"
            style={{
              left: `${st.x}%`, top: `${st.y}%`, width: st.s, height: st.s,
              background: '#F6E9C8', boxShadow: '0 0 8px 1px rgba(246,233,200,0.7)',
            }}
          />
        ))}
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: 0 }}
        transition={{ duration: 5, times: [0, 0.12, 0.85, 1] }}
        className="absolute inset-x-0 bottom-[86px] flex justify-center md:bottom-10"
      >
        <span className="rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#DCE3F7] shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(94,234,212,0.35)_inset]">
          ☾ {t('hud.tranceTitle', 'o transe da escrita — uma hora inteira')} ☽
        </span>
      </motion.div>
    </div>
  );
}

/* ─────────── noite mítica (2.000): as estrelas caem por você ───────────
   queda VERTICAL com rastro vertical — alinhamento perfeito por construção. */
const STARFALL = Array.from({ length: 14 }, (_, i) => ({
  left: 4 + ((i * 37) % 92), d: (i % 7) * 0.34, len: 70 + ((i * 13) % 50), s: 2.5 + (i % 2),
}));

export function MythicNightMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 5200);
  return (
    <div className="pointer-events-none fixed inset-0 z-[141] overflow-hidden" aria-hidden>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.8, 0.8, 0] }}
        transition={{ duration: 5, times: [0, 0.18, 0.82, 1] }}
        className="absolute inset-0 bg-[rgba(4,7,14,0.85)]"
      />
      {STARFALL.map((st, i) => (
        <motion.span
          key={i}
          initial={{ y: '-16vh', opacity: 0 }}
          animate={{ y: '104vh', opacity: [0, 1, 1, 0] }}
          transition={{ delay: 0.4 + st.d, duration: 1.5, ease: [0.4, 0, 0.7, 1] }}
          className="absolute top-0 rounded-full"
          style={{
            left: `${st.left}%`, width: st.s, height: st.s,
            background: '#F6E9C8', boxShadow: '0 0 10px 2.5px rgba(246,233,200,0.95)',
          }}
        >
          {/* rastro vertical, subindo da cabeça */}
          <span
            className="absolute bottom-full left-1/2 -translate-x-1/2"
            style={{
              width: 1.5, height: st.len,
              background: 'linear-gradient(0deg, rgba(246,233,200,0.95), rgba(246,233,200,0.25) 55%, transparent)',
            }}
          />
        </motion.span>
      ))}
      <div className="absolute inset-0 grid place-items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: [0, 1, 1, 0], y: 0 }}
          transition={{ duration: 4.6, times: [0, 0.18, 0.82, 1] }}
          className="text-center"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.34em]" style={{ color: 'var(--ember-hot)' }}>
            ✦ {t('hud.mythicEyebrow', 'noite mítica')}
          </p>
          <p className="mt-2 font-serif text-[26px] font-medium text-[#EBEEFA]" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.8)' }}>
            {t('hud.mythicTitle', 'Duas mil palavras numa noite.')}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

/* ─────────── a noite impossível (3.000): aurora boreal ─────────── */
const CURTAINS = [
  { left: 6, w: 30, c: 'rgba(94,234,212,0.2)', d: 0 },
  { left: 32, w: 34, c: 'rgba(167,139,250,0.16)', d: 0.5 },
  { left: 60, w: 32, c: 'rgba(94,234,212,0.17)', d: 1.0 },
];

export function AuroraNightMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 6200);
  return (
    <div className="pointer-events-none fixed inset-0 z-[141] overflow-hidden" aria-hidden>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.85, 0.85, 0] }}
        transition={{ duration: 6, times: [0, 0.18, 0.82, 1] }}
        className="absolute inset-0 bg-[rgba(4,7,14,0.88)]"
      />
      {CURTAINS.map((ct, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, scaleY: 0.6, skewX: -8 }}
          animate={{ opacity: [0, 1, 0.75, 0], scaleY: [0.6, 1, 0.9, 1], skewX: [-8, 4, -5, 2] }}
          transition={{ delay: 0.4 + ct.d, duration: 5, ease: 'easeInOut', times: [0, 0.35, 0.7, 1] }}
          className="absolute top-0 origin-top"
          style={{
            left: `${ct.left}%`, width: `${ct.w}%`, height: '44vh',
            background: `linear-gradient(180deg, ${ct.c}, transparent 82%)`,
            filter: 'blur(14px)',
          }}
        />
      ))}
      <div className="absolute inset-0 grid place-items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: [0, 1, 1, 0], y: 0 }}
          transition={{ duration: 5.6, times: [0, 0.16, 0.84, 1] }}
          className="text-center"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#5EEAD4]">
            ✧ {t('hud.auroraEyebrow', 'a noite impossível')}
          </p>
          <p className="mt-2 font-serif text-[26px] font-medium text-[#EBEEFA]" style={{ textShadow: '0 4px 30px rgba(0,0,0,0.8)' }}>
            {t('hud.auroraTitle', 'Três mil palavras. O céu dança por você.')}
          </p>
        </motion.div>
      </div>
    </div>
  );
}


/* ─────────── a íris: entrar no modo foco ─────────── */
export function IrisMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 2000);
  return (
    <div className="pointer-events-none fixed inset-0 z-[134]" aria-hidden>
      <motion.div
        initial={{ opacity: 0, scale: 1.6 }}
        animate={{ opacity: [0, 1, 0], scale: 1 }}
        transition={{ duration: 1.8, times: [0, 0.4, 1], ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at 50% 46%, transparent 30%, rgba(4,7,14,0.82) 78%)' }}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 0] }}
        transition={{ duration: 1.8, times: [0, 0.5, 1] }}
        className="absolute inset-x-0 bottom-[86px] flex justify-center md:bottom-10"
      >
        <span className="rounded-full bg-[rgba(13,20,38,0.94)] px-4 py-2 font-mono text-[9px] uppercase tracking-[0.24em] text-[#DCE3F7] shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
          ◎ {t('hud.irisTitle', 'só você e a página')}
        </span>
      </motion.div>
    </div>
  );
}

/* ─────────── vaga-lumes: escrevendo na madrugada ─────────── */
const FIREFLIES = Array.from({ length: 9 }, (_, i) => ({
  x: 6 + ((i * 53) % 88), wob: (i % 2 === 0 ? 1 : -1) * (8 + (i % 3) * 5), d: (i % 5) * 0.5, s: 3 + (i % 2),
}));

export function FirefliesMoment({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  useAutoDone(onDone, 5400);
  return (
    <div className="pointer-events-none fixed inset-0 z-[134] overflow-hidden" aria-hidden>
      {FIREFLIES.map((f, i) => (
        <motion.span
          key={i}
          initial={{ y: 0, x: 0, opacity: 0 }}
          animate={{ y: '-62vh', x: [0, f.wob, -f.wob * 0.6, f.wob * 0.4], opacity: [0, 0.9, 0.5, 0.9, 0] }}
          transition={{ delay: 0.3 + f.d, duration: 4.2, ease: 'easeInOut' }}
          className="absolute bottom-[-10px] rounded-full"
          style={{
            left: `${f.x}%`, width: f.s, height: f.s,
            background: '#F6E9C8', boxShadow: '0 0 10px 3px rgba(246,233,200,0.55)',
          }}
        />
      ))}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: 0 }}
        transition={{ duration: 4.8, times: [0, 0.15, 0.82, 1] }}
        className="absolute inset-x-0 bottom-[86px] flex justify-center md:bottom-10"
      >
        <span className="rounded-full bg-[rgba(13,20,38,0.94)] px-5 py-2.5 font-mono text-[9px] uppercase tracking-[0.24em] text-[#DCE3F7] shadow-[0_14px_40px_rgba(0,0,0,0.55),0_0_0_1px_rgba(168,184,232,0.14)_inset]">
          ✦ {t('hud.firefliesTitle', 'a madrugada é sua')}
        </span>
      </motion.div>
    </div>
  );
}

/* ─────────── banho de tema: a luz varre a tela ao trocar o tema ─────────── */
export function ThemeWashMoment({ toLight, onDone }: { toLight: boolean; onDone: () => void }) {
  useAutoDone(onDone, 1500);
  return (
    <div className="pointer-events-none fixed inset-0 z-[150] overflow-hidden" aria-hidden>
      <motion.div
        initial={{ x: '-120%' }}
        animate={{ x: '120%' }}
        transition={{ duration: 1.3, ease: [0.4, 0, 0.3, 1] }}
        className="absolute inset-y-0 w-[70%]"
        style={{
          background: toLight
            ? 'linear-gradient(100deg, transparent, rgba(247,241,224,0.5) 50%, transparent)'
            : 'linear-gradient(100deg, transparent, rgba(10,16,31,0.6) 50%, transparent)',
          filter: 'blur(8px)',
        }}
      />
    </div>
  );
}
